import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { notificationController } from '@app/controllers/notificationController';
import * as Auth from '@app/components/layouts/AuthLayout/AuthLayout.styles';
import { ReactComponent as Logo } from '@app/assets/images/Logo-Azul-Vertical.svg';
import { BaseFormInputItem } from '@app/components/common/forms/components/BaseFormInputItem/BaseFormInputItem';
import { InputPassword } from '@app/components/common/inputs/InputPassword/InputPassword';
import { Space } from 'antd';
import IAuthService, { AuthService } from '@app/services/authService';
import { readUser } from '@app/services/localStorage.service';

const authService: IAuthService = new AuthService();

interface FirstAccessFormData {
  password: string;
  confirmPassword: string;
}

export const FirstAccessForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [passErrorText, setPassErrorText] = useState('');
  const [validPass, setValidPass] = useState<boolean | undefined>();
  const [validConfirmPass, setValidConfirmPass] = useState<boolean | undefined>();
  const [firstAccessModel, setFirstAccessModel] = useState<FirstAccessFormData>({} as FirstAccessFormData);

  const handleSubmit = () => {
    if (validPass || validConfirmPass) {
      notificationController.error({ message: 'Senhas inválidas' });
    }

    setLoading(true);

    const user = readUser();
    authService
      .firstAccess({
        userId: user?.userId ?? '',
        novaSenha: firstAccessModel.password,
        confirmaNovaSenha: firstAccessModel.confirmPassword,
      })
      .then(() => {
        navigate('/auth/login');
        notificationController.success({
          message: t('newPassword.successMessage'),
          description: t('newPassword.successDescription'),
        });
        setLoading(false);
      })
      .catch((error) => {
        notificationController.error(error);
        setLoading(false);
      });
  };

  const validatePassword = (pass: string): boolean => {
    if (pass?.length < 6) {
      setValidPass(false);
      setPassErrorText('Senha pequena');
      return false;
    }

    if (!/(?=.*[A-Z]).*$/.test(pass)) {
      setValidPass(false);
      setPassErrorText('Senha deve conter letras maiúsculas');
      return false;
    }

    if (!/(?=.*[a-z]).*$/.test(pass)) {
      setValidPass(false);
      setPassErrorText('Senha deve conter letras maiúsculas');
      return false;
    }

    if (!/(?=.*[0-9]).*$/.test(pass)) {
      setValidPass(false);
      setPassErrorText('Senha deve conter números');
      return false;
    }

    if (!/(?=.*\W+)(?=^.{6,50}$).*$/.test(pass)) {
      setValidPass(false);
      setPassErrorText('Senha deve conter caracteres especiais');
      return false;
    }

    setValidPass(undefined);
    return true;
  };

  useEffect(() => {
    if (firstAccessModel.password) {
      validatePassword(firstAccessModel.password);
    }
  }, [firstAccessModel.password]);

  useEffect(() => {
    if (firstAccessModel.password != firstAccessModel.confirmPassword) {
      setValidConfirmPass(false);
    } else {
      setValidConfirmPass(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstAccessModel.confirmPassword]);

  return (
    <Auth.FormWrapper>
      <Logo style={{ marginBottom: '2rem' }} />
      <BaseForm layout="vertical" onFinish={handleSubmit} style={{ width: '398px' }}>
        <Auth.FormTitle>Primeiro acesso</Auth.FormTitle>
        <Auth.FormDescription>Defina uma nova senha para acesso.</Auth.FormDescription>
        <BaseFormInputItem
          label="Nova senha"
          name="password"
          supportText="Ex: mínimo de 6 caracteres com letras, números e caracteres especiais."
          isSuccess={validPass}
          errorText={passErrorText}
        >
          <InputPassword
            placeholder="Digite sua senha"
            type="password"
            onChange={(e) => setFirstAccessModel({ ...firstAccessModel, password: e.currentTarget.value })}
          />
        </BaseFormInputItem>
        <BaseFormInputItem
          label="Confirme sua nova senha"
          name="confirmPassword"
          isSuccess={validConfirmPass}
          supportText="Ex: mínimo de 8 caracteres com letras, números e caracteres especiais."
          errorText="Senhas diferentes"
        >
          <InputPassword
            placeholder="Digite sua senha"
            type="password"
            onChange={(e) => setFirstAccessModel({ ...firstAccessModel, confirmPassword: e.currentTarget.value })}
          />
        </BaseFormInputItem>
        <BaseForm.Item noStyle>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Auth.SubmitButton type="primary" htmlType="submit" loading={isLoading}>
              Criar senha
            </Auth.SubmitButton>
          </Space>
        </BaseForm.Item>
      </BaseForm>
    </Auth.FormWrapper>
  );
};
